import React, { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

const FREQS = ['Once', 'Daily', 'Weekly', 'Monthly', 'Yearly'];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface TicketControl {
  canSave: boolean;
  defaultName: string;
  savedFreq: string | null;
  saving: boolean;
  matchCount: number;
  onSave: (name: string, share: string[], freq: string) => Promise<boolean>;
}

interface Props extends TicketControl {
  open: boolean;
  triggerRef: (el: HTMLButtonElement | null) => void;
  onToggle: () => void;
  onClose: () => void;
}

const SaveTicket: React.FC<Props> = ({
  open,
  triggerRef,
  onToggle,
  onClose,
  canSave,
  defaultName,
  savedFreq,
  saving,
  matchCount,
  onSave,
}) => {
  const [ticketName, setTicketName] = useState(defaultName);
  const [share, setShare] = useState<string[]>([]);
  const [shareDraft, setShareDraft] = useState('');
  const [freq, setFreq] = useState('Once');

  useEffect(() => {
    if (open) {
      setTicketName(defaultName);
      setShare([]);
      setShareDraft('');
      setFreq(savedFreq || 'Once');
    }
  }, [open, defaultName, savedFreq]);

  const addShare = () => {
    const v = shareDraft.trim().replace(/,$/, '');
    if (EMAIL.test(v) && share.indexOf(v) === -1) {
      setShare([...share, v]);
      setShareDraft('');
    }
  };

  const save = async () => {
    if (await onSave(ticketName.trim(), share, freq)) {
      onClose();
    }
  };

  return (
    <div className='psc-pos'>
      <button
        ref={triggerRef}
        type='button'
        className={`psc-btn${savedFreq ? ' psc-btn--saved' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
        disabled={!canSave}
        title={canSave ? undefined : 'Saving is not available on this plan'}
      >
        {savedFreq ? (
          <CheckIcon sx={{ fontSize: 14 }} />
        ) : (
          <SaveOutlinedIcon sx={{ fontSize: 14 }} />
        )}
        {savedFreq ? 'Saved' : 'Save ticket'}
      </button>
      {open && (
        <div
          className='psc-pop psc-pop--right'
          role='dialog'
          aria-label='Save ticket'
        >
          <div className='psc-pop-sec psc-pop-sec--first'>
            <div className='psc-pop-title'>Save to Saved Work</div>
            <div className='psc-pop-lede'>
              Keeps the query, the filters and these{' '}
              <span className='tnum'>{matchCount.toLocaleString()}</span>{' '}
              {matchCount === 1 ? 'match' : 'matches'} as a ticket.
            </div>
          </div>
          <div className='psc-pop-sec'>
            <label htmlFor='psc-tn' className='psc-label'>
              TICKET NAME
            </label>
            <input
              id='psc-tn'
              type='text'
              className='psc-field psc-field--tall psc-pop-field'
              value={ticketName}
              onChange={(e) => setTicketName(e.target.value)}
            />
          </div>
          <div className='psc-pop-sec'>
            <label htmlFor='psc-share' className='psc-label'>
              SHARE WITH
            </label>
            {share.length > 0 && (
              <div className='psc-share-list'>
                {share.map((em) => (
                  <span key={em} className='psc-fchip psc-fchip--share'>
                    {em}
                    <button
                      type='button'
                      className='psc-x'
                      aria-label={`Remove ${em}`}
                      onClick={() => setShare(share.filter((x) => x !== em))}
                    >
                      <CloseIcon sx={{ fontSize: 11 }} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              id='psc-share'
              type='email'
              className='psc-field psc-field--tall psc-pop-field'
              placeholder='Teammate’s email, then Enter'
              value={shareDraft}
              onChange={(e) => setShareDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addShare();
                }
              }}
              onBlur={addShare}
            />
          </div>
          <div className='psc-pop-sec psc-pop-sec--last'>
            <div className='psc-label'>RE-SCREEN</div>
            <div
              className='psc-seg psc-seg--wide psc-pop-field'
              role='group'
              aria-label='Re-screen frequency'
            >
              {FREQS.map((f) => (
                <button
                  key={f}
                  type='button'
                  aria-pressed={freq === f}
                  onClick={() => setFreq(f)}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className='psc-pop-hint'>
              {freq === 'Once'
                ? 'Saved as it stands today. It will not re-screen on its own.'
                : `Re-screens ${freq.toLowerCase()} and flags new matches in Saved Work.`}
            </div>
          </div>
          <div className='psc-pop-foot'>
            <button
              type='button'
              className='psc-btn psc-btn--sm'
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type='button'
              className='psc-primary psc-primary--sm'
              disabled={!ticketName.trim() || saving}
              onClick={save}
            >
              {saving ? 'Saving…' : 'Save ticket'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaveTicket;
